import { createAsyncThunk } from '@reduxjs/toolkit';
// import { sendGetRequest } from './requestUtil';
import {
  sendDeleteRequest,
  sendGetRequest,
  sendPostRequest,
} from '../../../api/requestUtil';
import { CuratedList, UserSchema } from '../../curatedLists/types/curatedLists.types';
import { PaginatedResponse, ProfileDiscussion, FetchPaginatedDataParams, ProfileComment, ProfileUpvotedBlog, ProfileCuratedList } from '../types/profile.types';

export const uploadProfilePicture = createAsyncThunk(
  'upload-profile-picture',
  async (body: FormData, { getState }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    return sendPostRequest('/api/upload-profile-picture/', body, token);
  }
);

export const createUpdateProfile = createAsyncThunk(
  'create-profile',
  async (body: CreateProfileType, { getState }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    return sendPostRequest('/api/profile-create-update/', body, token);
  }
);

export const getAllSubjectTags = createAsyncThunk('get-tags', async () => {
  return sendGetRequest('/api/tags/');
});

export const getBookmarkedBlogs = createAsyncThunk(
  'get-bookmarked-blogs',
  async (_: void, { getState }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    const startVersion = state.profile.bookmarksVersion ?? 0;
    const response = await sendGetRequest('/api/blogs/bookmarks/', token);
    return { ...response, startVersion };
  }
);

export const saveBlogBookmark = createAsyncThunk(
  'save-blog-bookmark',
  async (body: any, { getState }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    return sendPostRequest('/api/blog/bookmark/', body, token);
  }
);

export const deleteBookmark = createAsyncThunk(
  'delete-bookmark',
  async (body: { blogId: number }, { getState, dispatch }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    try {
      return await sendDeleteRequest(
        `/api/blog/bookmark/?blogId=${body.blogId}`,
        token
      );
    } catch (error) {
      // The slice removed the bookmark optimistically; restore the real list.
      dispatch(getBookmarkedBlogs());
      throw error;
    }
  }
);


export const getUserDiscussionsAndComments = createAsyncThunk(
  'get-user-discussions-comments',
  async (_, { getState }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    return sendGetRequest('api/user-discussions-comments/', token);
  }
);

export const getUserUpvotedBlogs = createAsyncThunk(
  'get-user-upvoted-blogs',
  async (_, { getState }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    return sendGetRequest('api/user-upvoted-blogs/', token);
  }
);


export const getUserCuratedLists = createAsyncThunk<
  CuratedList[], 
  void,
  { rejectValue: string }
>(
  'get-user-curated-lists',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;
      const token = state.user.authToken;
      const username = state.user.username;
      
      if (!username) {
        console.error('No username found in state');
        return rejectWithValue('Username not found');
      }
      
      // console.log('Fetching curated lists for user:', username);
      
      const endpoint = `/api/curation/users/${username}/lists/`;
      
      const response = await sendGetRequest(endpoint, token) as any[];
      
      // console.log('Raw curated lists response:', response);
      
      if (Array.isArray(response)) {
        // Normalize the response to ensure creator is always UserSchema object
        const normalizedLists: CuratedList[] = response.map(list => {
          // Handle case where creator might be string from old API
          let creator: UserSchema;
          
          if (typeof list.creator === 'string') {
            // Create a minimal UserSchema from the username string
            creator = {
              id: 0, // We don't have the ID from string format
              username: list.creator,
            };
            console.warn(`List ${list.id} has string creator, converting to object`);
          } else if (list.creator && typeof list.creator === 'object' && 'username' in list.creator) {
            // Already an object, use it directly
            creator = list.creator as UserSchema;
          } else {
            // Fallback - create from current user data
            creator = {
              id: state.user.userId || 0,
              username: state.user.username || 'unknown',
            };
            console.warn(`List ${list.id} has invalid creator format:`, list.creator);
          }
          
          return {
            ...list,
            creator,
            items: list.items || [] // Ensure items is always an array
          };
        });
        
        console.log('Normalized lists:', normalizedLists);
        return normalizedLists;
      } 
      
      console.warn('Unexpected response format (not an array):', response);
      return [];
      
    } catch (error: any) {
      console.error('Error fetching curated lists:', error);
      return rejectWithValue(error.message || 'Failed to fetch curated lists');
    }
  }
);

export const getUserDiscussionsPaginated = createAsyncThunk<
  PaginatedResponse<ProfileDiscussion>,
  FetchPaginatedDataParams,
  { rejectValue: string }
>(
  'get-user-discussions-paginated',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;
      const token = state.user.authToken;
      const username = state.user.username;

      if (!username) {
        return rejectWithValue('Username not found');
      }

      const endpoint = `/api/users/${username}/discussions/?page=${page}`;
      const response = await sendGetRequest(endpoint, token) as PaginatedResponse<ProfileDiscussion>;
      return response;
    } catch (error: any) {
      console.error('Error fetching discussions:', error);
      return rejectWithValue(error.message || 'Failed to fetch discussions');
    }
  }
);

export const getUserCommentsPaginated = createAsyncThunk<
  PaginatedResponse<ProfileComment>,
  FetchPaginatedDataParams,
  { rejectValue: string }
>(
  'get-user-comments-paginated',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;
      const token = state.user.authToken;
      const username = state.user.username;

      if (!username) {
        return rejectWithValue('Username not found');
      }

      const endpoint = `/api/users/${username}/comments/?page=${page}`;
      const response = await sendGetRequest(endpoint, token) as PaginatedResponse<ProfileComment>;
      return response;
    } catch (error: any) {
      console.error('Error fetching comments:', error);
      return rejectWithValue(error.message || 'Failed to fetch comments');
    }
  }
);

export const getUserUpvotedBlogsPaginated = createAsyncThunk<
  PaginatedResponse<ProfileUpvotedBlog>,
  FetchPaginatedDataParams,
  { rejectValue: string }
>(
  'get-user-upvoted-blogs-paginated',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;
      const token = state.user.authToken;
      const username = state.user.username;

      if (!username) {
        return rejectWithValue('Username not found');
      }

      const endpoint = `/api/users/${username}/upvoted-blogs/?page=${page}`;
      const response = await sendGetRequest(endpoint, token) as PaginatedResponse<ProfileUpvotedBlog>;
      return response;
    } catch (error: any) {
      console.error('Error fetching upvoted blogs:', error);
      return rejectWithValue(error.message || 'Failed to fetch upvoted blogs');
    }
  }
);

export const getUserCuratedListsPaginated = createAsyncThunk<
  PaginatedResponse<ProfileCuratedList>,
  FetchPaginatedDataParams,
  { rejectValue: string }
>(
  'get-user-curated-lists-paginated',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as AppState;
      const token = state.user.authToken;
      const username = state.user.username;

      if (!username) {
        return rejectWithValue('Username not found');
      }

      const endpoint = `/api/users/${username}/curated-lists/?page=${page}`;
      const response = await sendGetRequest(endpoint, token) as PaginatedResponse<ProfileCuratedList>;
      return response;
    } catch (error: any) {
      console.error('Error fetching curated lists:', error);
      return rejectWithValue(error.message || 'Failed to fetch curated lists');
    }
  }
);